import { FloatingFormItem } from '@/components/_common';
import { PurchaseItemsFormValues } from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Divider, Form, FormInstance, Row, Select } from 'antd';
import { useIntl } from 'react-intl';
import IcmsFields from './IcmsFields';
import IcmsStFields from './IcmsStFields';
import { useReleasePurchaseFormContext } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';
import TaxRegimeSelect from '@/components/_common/TaxRegimeSelect';
import { TaxRegimeEnum } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';

interface Props {
  form: FormInstance<PurchaseItemsFormValues>;
}

const IcmsForm: React.FC<Props> = ({ form }) => {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const { purchase } = useReleasePurchaseFormContext();

  const taxRegime = Form.useWatch(['purchaseItemTaxes', 'taxRegime'], form);

  const getOptions = (codes: { id: number; code: string }[]) => {
    return codes.map((code) => ({
      value: code.code,
      label: code.code,
    }));
  };

  const meiOptions = ['102', '103', '300', '400', '900'];

  const cstCodes = [
    { id: 1, code: '00' },
    { id: 2, code: '10' },
    { id: 3, code: '20' },
    { id: 4, code: '30' },
    { id: 5, code: '40' },
    { id: 6, code: '41' },
    { id: 7, code: '50' },
    { id: 8, code: '51' },
    { id: 9, code: '60' },
    { id: 10, code: '70' },
    { id: 11, code: '90' },
  ];

  const csosnCodes = [
    { id: 1, code: '101' },
    { id: 2, code: '102' },
    { id: 3, code: '103' },
    { id: 4, code: '201' },
    { id: 5, code: '202' },
    { id: 6, code: '203' },
    { id: 7, code: '300' },
    { id: 8, code: '400' },
    { id: 9, code: '500' },
    { id: 10, code: '900' },
  ];

  const originIcmsOptions = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
  ];

  const filteredCsosnCodes =
    taxRegime === TaxRegimeEnum.simplesNationalMei
      ? csosnCodes.filter((item) => meiOptions.includes(item.code))
      : csosnCodes;

  const handleFieldsActive = (
    cstIcmsParam: string[],
    csosnIcmsParam?: string[],
  ) => {
    const cstIcmsField = form.getFieldValue(['purchaseItemTaxes', 'cstIcms']);
    const csosnIcmsField = form.getFieldValue([
      'purchaseItemTaxes',
      'csosnIcms',
    ]);

    return cstIcmsField
      ? cstIcmsParam.includes(cstIcmsField)
      : csosnIcmsParam?.includes(csosnIcmsField) || false;
  };

  const handleValuesChanged = (values: PurchaseItemsFormValues) => {
    if (values.purchaseItemTaxes?.taxRegime) {
      form.resetFields([
        ['purchaseItemTaxes', 'csosnIcms'],
        ['purchaseItemTaxes', 'cstIcms'],
      ]);
    }
  };

  const csosnIcmsSelect = (
    <FloatingFormItem
      name={['purchaseItemTaxes', 'csosnIcms']}
      label={intl.formatMessage({
        id: 'purchases.details.itemsPurchase.icms.taxRegime.csosn',
      })}
      rules={taxRegime ? [defaultEmptyFieldError] : []}
    >
      <Select
        options={getOptions(filteredCsosnCodes)}
        disabled={purchase?.boschMigration}
      />
    </FloatingFormItem>
  );

  const cstIcmsSelect = (
    <FloatingFormItem
      name={['purchaseItemTaxes', 'cstIcms']}
      label="CST"
      rules={taxRegime ? [defaultEmptyFieldError] : []}
    >
      <Select
        options={getOptions(cstCodes)}
        disabled={purchase?.boschMigration}
      />
    </FloatingFormItem>
  );

  return (
    <Form form={form} onValuesChange={handleValuesChanged}>
      <Row gutter={[12, 12]}>
        <Col lg={8} span={24} md={12}>
          <FloatingFormItem
            name={['purchaseItemTaxes', 'taxRegime']}
            label={intl.formatMessage({
              id: 'purchases.details.itemsPurchase.icms.taxRegime',
            })}
          >
            <TaxRegimeSelect
              isPurchase
              allowClear
              style={{ width: '100%' }}
              disabled={purchase?.boschMigration}
            />
          </FloatingFormItem>
        </Col>

        <Col lg={2} span={24} md={12}>
          {taxRegime ===
            TaxRegimeEnum.simpleNationalExcessSublimitGrossRevenue ||
          taxRegime === TaxRegimeEnum.normal
            ? cstIcmsSelect
            : csosnIcmsSelect}
        </Col>

        <Col lg={14} span={24}>
          <FloatingFormItem
            name={['purchaseItemTaxes', 'originIcms']}
            label={intl.formatMessage({
              id: 'purchases.details.itemsPurchase.icms.taxRegime.originIcms',
            })}
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={originIcmsOptions.map((value, index) => ({
                value: index,
                label: intl.formatMessage({
                  id: `purchases.details.itemsPurchase.icms.taxRegime.originIcms.${value}`,
                }),
              }))}
              disabled={purchase?.boschMigration}
            />
          </FloatingFormItem>
        </Col>

        <Divider style={{ margin: 0 }} />

        <Col lg={24} span={24}>
          <IcmsFields enable={handleFieldsActive} />
        </Col>

        <Divider style={{ margin: 0 }} />

        <Col lg={24} span={24}>
          <IcmsStFields enable={handleFieldsActive} />
        </Col>
      </Row>
    </Form>
  );
};

export default IcmsForm;
